import {
  Button,
  colors,
  Icon,
  Popover,
  SkeletonButton,
  TextInput,
} from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import { DropdownSwitcherContent } from 'src/core/common/components/DropdownSwitcherContent';
import { QueryError } from 'src/core/common/components/QueryError';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { BudgetPeriodStatusTag } from 'src/core/modules/budgets/components/BudgetPeriodStatusTag';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { useBudgetaryExercisesQuery } from '../../../../containers/hooks/useBudgetaryExercisesQuery';
import {
  type BudgetaryExercise,
  getBudgetaryExerciseAmount,
  getBudgetaryExercisePeriod,
} from '../../../../models/budgetaryExercise';

type Props = {
  currentBudgetaryExercise: BudgetaryExercise;
  onBudgetaryExerciseSelect: (budgetaryExerciseId: string) => void;
  onCreateBudgetaryExercise: () => void;
  isDisabled?: boolean;
};

export const BudgetaryExerciseDropdown = ({
  currentBudgetaryExercise,
  onCreateBudgetaryExercise,
  onBudgetaryExerciseSelect,
  isDisabled = false,
}: Props) => {
  const { t } = useTranslation('global');
  const budgetaryExercisesQueryState = useBudgetaryExercisesQuery();
  const { isAccountOwner, isController } = useUserRoles();
  const isAccountOwnerOrController = isController || isAccountOwner;
  const canCreateBudgets = isAccountOwnerOrController;
  const [searchQuery, setSearchQuery] = useState('');

  if (budgetaryExercisesQueryState.status === 'loading') {
    return <SkeletonButton width="100px" />;
  }

  if (budgetaryExercisesQueryState.status === 'error') {
    return (
      <QueryError
        queryError={budgetaryExercisesQueryState.error}
        componentType="Callout"
      />
    );
  }

  const otherBudgetaryExercises = budgetaryExercisesQueryState.data?.filter(
    (budgetaryExercise) => budgetaryExercise.id !== currentBudgetaryExercise.id,
  );

  const getBudgetaryExerciseDropdownListItem = (
    exercise: BudgetaryExercise,
  ) => {
    const budgetaryExerciseAmount = getBudgetaryExerciseAmount(exercise);

    return {
      id: exercise.id,
      left: (
        <div className="mr-8 flex items-center">
          <span
            className="mr-8 flex-initial truncate"
            data-testid="BudgetaryExerciseDropdownListItem"
          >
            {exercise.name}
          </span>
          <BudgetPeriodStatusTag
            period={getBudgetaryExercisePeriod(exercise)}
          />
        </div>
      ),
      right: budgetaryExerciseAmount
        ? formatMonetaryValue(budgetaryExerciseAmount)
        : undefined,
    };
  };

  return (
    <Popover
      className="CompanyDropdown"
      placement="bottom-end"
      renderTrigger={(triggerProps) => {
        return (
          <Button
            {...triggerProps}
            variant="secondaryNeutral"
            text={currentBudgetaryExercise.name}
            hasNegativeMargins
            isDisabled={isDisabled}
            data-testid="BudgetaryExercisesPageContainerButton"
          />
        );
      }}
    >
      {(closeDropdown) => (
        <div>
          <div className="p-16 pb-0">
            <TextInput
              placeholder={t('budget.budgetaryExercise.searchPlaceholder')}
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
              leftAddon={
                <Icon
                  name="magnifying-glass"
                  color={colors.contentSecondaryBgPrimary}
                  className="ml-8"
                />
              }
              fit="parent"
            />
          </div>
          <DropdownSwitcherContent
            activeItem={getBudgetaryExerciseDropdownListItem(
              currentBudgetaryExercise,
            )}
            items={otherBudgetaryExercises
              .filter((otherBudgetaryExercise) =>
                otherBudgetaryExercise.name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()),
              )
              .map((otherBudgetaryExercise) => ({
                ...getBudgetaryExerciseDropdownListItem(otherBudgetaryExercise),
                onClick: () => {
                  onBudgetaryExerciseSelect(otherBudgetaryExercise.id);
                  closeDropdown();
                },
              }))}
            button={
              canCreateBudgets
                ? {
                    label: t('budget.budgetaryExercise.createButton'),
                    onClick: () => {
                      onCreateBudgetaryExercise();
                      closeDropdown();
                    },
                  }
                : undefined
            }
          />
        </div>
      )}
    </Popover>
  );
};
